import type { Section } from '../types';

/**
 * Ops shell strings. Spanish is the default and is what the crew reads: third
 * grade reading level, one instruction per line, Panamanian vocabulary
 * (abanico, pluma, paño, de una vez), tu form, warm but direct.
 *
 * The English side is for the owner console and for Tim. It is a real
 * translation, never a label without a verb.
 */
export const ops = {
  'ops.nav.today': { es: 'Hoy', en: 'Today' },
  'ops.nav.cleaning': { es: 'Limpieza', en: 'Housekeeping' },
  'ops.nav.maintenance': { es: 'Mantenimiento', en: 'Maintenance' },
  'ops.nav.systems': { es: 'Sistemas', en: 'Systems' },
  'ops.nav.messages': { es: 'Mensajes', en: 'Messages' },
  'ops.nav.translator': { es: 'Traductor', en: 'Translator' },
  'ops.nav.owner': { es: 'Dueño', en: 'Owner console' },

  'ops.login.title': { es: 'Entra con tu PIN', en: 'Sign in with your PIN' },
  'ops.login.pin_label': { es: 'Tu PIN', en: 'Your PIN' },
  'ops.login.submit': { es: 'Entrar', en: 'Sign in' },
  'ops.login.wrong_pin': {
    es: 'Ese PIN no es. Te quedan {left} intentos.',
    en: 'That PIN is wrong. You have {left} tries left.',
  },
  'ops.login.locked': {
    es: 'Espera {minutes} minutos y prueba otra vez.',
    en: 'Wait {minutes} minutes and try again.',
  },
  'ops.login.not_paired': {
    es: 'Este teléfono no está registrado. Pídele el enlace a tu jefe.',
    en: 'This phone is not paired. Ask your manager for the link.',
  },
  'ops.pair.title': { es: 'Registrar este teléfono', en: 'Pair this phone' },
  'ops.pair.done': {
    es: 'Listo. Ahora entras solo con tu PIN.',
    en: 'Done. From now on your PIN is enough.',
  },

  'ops.today.in_house': { es: 'En la isla', en: 'On the island' },
  'ops.today.arrivals': { es: 'Llegan hoy', en: 'Arriving today' },
  'ops.today.departures': { es: 'Se van hoy', en: 'Leaving today' },
  'ops.today.my_jobs': { es: 'Tu trabajo de hoy', en: 'Your jobs today' },
  'ops.today.open_requests': { es: 'Pedidos abiertos', en: 'Open requests' },
  'ops.today.nothing': {
    es: 'Hoy no tienes trabajo asignado.',
    en: 'You have no job assigned today.',
  },

  'ops.cleaning.pick_job': { es: 'Escoge el trabajo', en: 'Pick the job' },
  'ops.cleaning.start': { es: 'Empezar', en: 'Start' },
  'ops.cleaning.finish': { es: 'Terminé', en: 'I am done' },
  'ops.cleaning.photo_required': {
    es: 'Esta tarea lleva foto.',
    en: 'This task needs a photo.',
  },
  'ops.cleaning.problem_question': {
    es: '¿Algo mal en este cuarto?',
    en: 'Anything wrong in this room?',
  },
  'ops.cleaning.problem_yes': { es: 'Sí', en: 'Yes' },
  'ops.cleaning.problem_no': { es: 'No', en: 'No' },
  'ops.cleaning.reference_photo': { es: 'Así queda bien', en: 'This is how it should look' },
  'ops.cleaning.redo': {
    es: 'Hay que repetir esto:',
    en: 'This has to be done again:',
  },
  'ops.cleaning.approved': { es: 'Aprobado. Gracias.', en: 'Approved. Thank you.' },

  'ops.maintenance.title': { es: 'Reportar algo dañado', en: 'Report something broken' },
  'ops.maintenance.pick_asset': { es: '¿Qué es?', en: 'What is it?' },
  'ops.maintenance.pick_place': { es: '¿Dónde está?', en: 'Where is it?' },
  'ops.maintenance.voice_note': { es: 'Grabar una nota de voz', en: 'Record a voice note' },
  'ops.maintenance.sent': { es: 'Enviado. Gracias.', en: 'Sent. Thank you.' },

  'ops.systems.if_it_fails': {
    es: 'Si falla, haz esto primero y después llama.',
    en: 'If it fails, do this first, then call.',
  },
  'ops.systems.watch_video': { es: 'Ver el video', en: 'Watch the video' },

  /* ---- Navigation ---- */
  'ops.nav.label': { es: 'Menú principal', en: 'Main menu' },
  'ops.nav.cleaning_short': { es: 'Limpieza', en: 'Cleaning' },
  'ops.nav.report_short': { es: 'Reportar un problema', en: 'Report a problem' },
  'ops.nav.more': { es: 'Más', en: 'More' },
  'ops.nav.boss': { es: 'Para jefes', en: 'For managers' },
  'ops.more.title': { es: 'Más', en: 'More' },
  'ops.more.messages_hint': {
    es: 'Habla con el equipo sobre un pedido.',
    en: 'Talk with the team about a request.',
  },
  'ops.more.translator_hint': {
    es: 'Escribe o di algo y te lo paso al otro idioma.',
    en: 'Type or say something and get it in the other language.',
  },
  'ops.more.rooms_hint': { es: 'Quién duerme en cada cuarto.', en: 'Who sleeps in each room.' },
  'ops.more.boss_hint': {
    es: 'Estadías, teléfonos y revisar limpiezas.',
    en: 'Stays, phones and reviewing cleanings.',
  },
  'ops.more.owner_hint': {
    es: 'Entrada del dueño, con correo y clave.',
    en: 'Owner sign in with email and password.',
  },

  /* ---- Sign in, pairing, owner ---- */
  'ops.login.have_link': { es: 'Tengo un enlace o un código', en: 'I have a link or a code' },
  'ops.login.owner_link': { es: 'Soy dueño: entrar con correo', en: 'I am an owner: sign in with email' },
  'ops.pair.no_code': {
    es: 'Abre el enlace que te mandó tu jefe.',
    en: 'Open the link your manager sent you.',
  },
  'ops.pair.paste_label': {
    es: 'O pega aquí el enlace o el código',
    en: 'Or paste the link or the code here',
  },
  'ops.pair.paste_submit': { es: 'Registrar este teléfono', en: 'Pair this phone' },
  'ops.pair.bad_code': {
    es: 'Ese código no se ve bien. Copia el enlace completo.',
    en: 'That code does not look right. Copy the whole link.',
  },
  'ops.pair.already': {
    es: 'Este teléfono ya está registrado.',
    en: 'This phone is already paired.',
  },
  'ops.owner.title': { es: 'Entrada del dueño', en: 'Owner sign in' },
  'ops.owner.email': { es: 'Correo', en: 'Email' },
  'ops.owner.password': { es: 'Clave', en: 'Password' },
  'ops.owner.submit': { es: 'Entrar', en: 'Sign in' },
  'ops.owner.wrong': {
    es: 'El correo o la clave no son. Revisa y prueba otra vez.',
    en: 'The email or the password is wrong. Check and try again.',
  },
  'ops.owner.no_signal': {
    es: 'No hay señal. Prueba cuando vuelva.',
    en: 'No signal. Try again when it comes back.',
  },
  'ops.owner.signed_in': { es: 'Entraste como {email}.', en: 'Signed in as {email}.' },
  'ops.owner.to_console': { es: 'Ir al inicio', en: 'Go to the home screen' },
  'ops.owner.to_pin': { es: 'Entrar con PIN', en: 'Sign in with a PIN' },
  'ops.owner.forgot': { es: '¿Olvidaste tu clave?', en: 'Forgot your password?' },
  'ops.owner.forgot_needs_email': {
    es: 'Escribe tu correo arriba primero.',
    en: 'Type your email above first.',
  },
  'ops.owner.forgot_sent': {
    es: 'Si esa cuenta existe, te enviamos un enlace para cambiar la clave.',
    en: 'If that account exists, we sent a link to change the password.',
  },
  'ops.owner.reset_title': { es: 'Nueva clave', en: 'New password' },
  'ops.owner.reset_password': { es: 'Nueva clave', en: 'New password' },
  'ops.owner.reset_confirm': { es: 'Escribe la clave otra vez', en: 'Type the password again' },
  'ops.owner.reset_submit': { es: 'Guardar la clave', en: 'Save password' },
  'ops.owner.reset_mismatch': {
    es: 'Las dos claves no son iguales.',
    en: 'The two passwords do not match.',
  },
  'ops.owner.reset_rejected': {
    es: 'El servidor no aceptó esa clave: {reason}',
    en: 'The server did not accept that password: {reason}',
  },
  'ops.owner.show': { es: 'Mostrar', en: 'Show' },
  'ops.owner.hide': { es: 'Ocultar', en: 'Hide' },
  'ops.owner.reset_success': {
    es: 'Clave guardada. Ya puedes entrar.',
    en: 'Password saved. You can sign in now.',
  },
  'ops.owner.reset_expired': {
    es: 'Este enlace ya no sirve. Pide uno nuevo desde la entrada del dueño.',
    en: 'This link no longer works. Ask for a new one from the owner sign-in page.',
  },
  'ops.boss.title': { es: 'Para jefes', en: 'For managers' },
  'ops.boss.not_allowed': {
    es: 'Esta parte es solo para los jefes.',
    en: 'This part is only for managers.',
  },

  /* ---- Hoy ---- */
  'ops.today.hello': { es: 'Hola, {name}', en: 'Hi, {name}' },
  'ops.today.people_only_adults': {
    es: '{adults} adultos en la isla.',
    en: '{adults} adults on the island.',
  },
  'ops.today.people_with_children': {
    es: '{people} en la isla: {adults} adultos, {children} niños.',
    en: '{people} on the island: {adults} adults, {children} kids.',
  },
  'ops.today.nobody': {
    es: 'Nadie en la isla ahora mismo.',
    en: 'Nobody on the island right now.',
  },
  'ops.today.leave_on': { es: 'Se van el {date}.', en: 'Leaving {date}.' },
  'ops.today.arrivals_count': { es: '{count} llegan hoy', en: '{count} arriving today' },
  'ops.today.departures_count': { es: '{count} se van hoy', en: '{count} leaving today' },
  'ops.today.start_cleaning': { es: 'Empezar una limpieza', en: 'Start a cleaning' },
  'ops.today.requests_count': {
    es: 'Hay {count} pedidos abiertos.',
    en: 'There are {count} open requests.',
  },
  'ops.today.requests_count_one': {
    es: 'Hay 1 pedido abierto.',
    en: 'There is 1 open request.',
  },
  'ops.today.no_requests': { es: 'No hay pedidos abiertos.', en: 'There are no open requests.' },
  'ops.today.see_messages': { es: 'Ver los pedidos', en: 'See the requests' },
  'ops.today.weather': { es: 'El clima', en: 'The weather' },
  'ops.today.weather_none': {
    es: 'Sin datos del clima por ahora.',
    en: 'No weather data for now.',
  },
  'ops.today.weather_at': { es: 'Visto a las {time}.', en: 'Seen at {time}.' },
  'ops.today.wet_warning': {
    es: 'Hay lluvia o tormenta. Ten cuidado en los muelles.',
    en: 'There is rain or a storm. Take care on the docks.',
  },
  'ops.today.stale': {
    es: 'Datos guardados en el teléfono. Pueden estar viejos.',
    en: 'Data saved on the phone. It may be old.',
  },

  /* ---- Phases and request words ---- */
  'ops.phase.working': { es: 'Trabajando', en: 'In progress' },
  'ops.phase.waiting': { es: 'Esperando revisión', en: 'Waiting for review' },
  'ops.phase.redo': { es: 'Hay que repetir', en: 'Needs a redo' },
  'ops.phase.approved': { es: 'Aprobado', en: 'Approved' },
  'ops.request.status.new': { es: 'Nuevo', en: 'New' },
  'ops.request.status.seen': { es: 'Visto', en: 'Seen' },
  'ops.request.status.in_progress': { es: 'En proceso', en: 'In progress' },
  'ops.request.status.done': { es: 'Listo', en: 'Done' },
  'ops.request.status.declined': { es: 'No se hace', en: 'Not doing it' },
  'ops.request.type.boat': { es: 'Lancha', en: 'Boat' },
  'ops.request.type.provisioning': { es: 'Compras', en: 'Provisioning' },
  'ops.request.type.maintenance': { es: 'Arreglo', en: 'Repair' },
  'ops.request.type.housekeeping': { es: 'Limpieza', en: 'Housekeeping' },
  'ops.request.type.concierge': { es: 'Atención', en: 'Concierge' },
  'ops.request.type.other': { es: 'Otro', en: 'Other' },

  /* ---- Limpieza ---- */
  'ops.cleaning.pick_house': { es: '¿Qué casa?', en: 'Which house?' },
  'ops.cleaning.pick_list': { es: '¿Qué lista?', en: 'Which list?' },
  'ops.cleaning.my_jobs': { es: 'Tus trabajos', en: 'Your jobs' },
  'ops.cleaning.no_jobs': { es: 'Todavía no has empezado ninguno.', en: 'You have not started any yet.' },
  'ops.cleaning.continue': { es: 'Seguir con este', en: 'Continue this one' },
  'ops.cleaning.start_new': { es: 'Empezar un trabajo nuevo', en: 'Start a new job' },
  'ops.cleaning.no_catalog': {
    es: 'Todavía no hay listas en este teléfono. Conéctate una vez para bajarlas.',
    en: 'There are no lists on this phone yet. Connect once to download them.',
  },
  'ops.cleaning.progress': { es: '{done} de {total} listas', en: '{done} of {total} done' },
  'ops.cleaning.next': { es: 'Sigue con esto', en: 'Next up' },
  'ops.cleaning.done_button': { es: 'Hecho', en: 'Done' },
  'ops.cleaning.done_word': { es: 'Hecho', en: 'Done' },
  'ops.cleaning.photo_saved': { es: 'Foto guardada', en: 'Photo saved' },
  'ops.cleaning.photo_missing': { es: 'Falta la foto', en: 'Photo is missing' },
  'ops.cleaning.problem_note': { es: '¿Qué está mal? (si quieres)', en: 'What is wrong? (optional)' },
  'ops.cleaning.problem_photo': { es: 'Toma una foto del daño.', en: 'Take a photo of the damage.' },
  'ops.cleaning.problem_sent': {
    es: 'Avisamos a mantenimiento.',
    en: 'We told maintenance.',
  },
  'ops.cleaning.problem_none': { es: 'Todo bien aquí.', en: 'All good here.' },
  'ops.cleaning.stage_open': { es: 'Al llegar a la casa', en: 'When you arrive at the house' },
  'ops.cleaning.stage_mop': { es: 'Trapea toda la casa', en: 'Mop the whole house' },
  'ops.cleaning.stage_close': { es: 'Antes de irte', en: 'Before you leave' },
  'ops.cleaning.finish_blocked_cards': {
    es: 'Todavía faltan {count} tarjetas.',
    en: '{count} cards are still open.',
  },
  'ops.cleaning.finish_blocked_photos': {
    es: 'Todavía faltan {count} fotos.',
    en: '{count} photos are still missing.',
  },
  'ops.cleaning.finished': {
    es: 'Listo. Tu jefe lo va a revisar.',
    en: 'Done. Your manager will review it.',
  },
  'ops.cleaning.finish_again': { es: 'Terminé de nuevo', en: 'I am done again' },
  'ops.cleaning.redo_help': {
    es: 'Repite lo que dice la nota. Después toca Terminé de nuevo.',
    en: 'Redo what the note says. Then tap I am done again.',
  },
  'ops.cleaning.repeat': { es: 'Repetir con foto', en: 'Repeat with a photo' },
  'ops.cleaning.waiting': {
    es: 'Esperando que tu jefe lo revise.',
    en: 'Waiting for your manager to review it.',
  },
  'ops.cleaning.review_note': { es: 'Nota de tu jefe', en: 'Note from your manager' },
  'ops.cleaning.not_found': {
    es: 'No encuentro este trabajo en el teléfono.',
    en: 'I cannot find this job on the phone.',
  },
  'ops.cleaning.room_intro': { es: 'Cuarto: {room}', en: 'Room: {room}' },
  'ops.cleaning.house_wide': { es: 'Toda la casa', en: 'The whole house' },
  'ops.cleaning.checking_in': { es: 'Creando el trabajo', en: 'Creating the job' },
  'ops.cleaning.order_note': {
    es: 'Primero los daños, luego la ropa, y al final el trapeo.',
    en: 'Damage first, then laundry, mopping at the end.',
  },

  /* ---- Mantenimiento ---- */
  'ops.maintenance.what_tab': { es: 'Un equipo', en: 'A machine or system' },
  'ops.maintenance.where_tab': { es: 'Un cuarto o lugar', en: 'A room or place' },
  'ops.maintenance.pick_none': { es: 'Escoge uno', en: 'Pick one' },
  'ops.maintenance.pick_other': { es: 'Otro lugar', en: 'Somewhere else' },
  'ops.maintenance.other_place_label': { es: 'Escribe dónde', en: 'Type where' },
  'ops.maintenance.photo_title': { es: 'Toma una foto', en: 'Take a photo' },
  'ops.maintenance.note_label': { es: 'Cuéntanos qué pasa (si quieres)', en: 'Tell us what is wrong (optional)' },
  'ops.maintenance.urgent': { es: 'Es urgente', en: 'It is urgent' },
  'ops.maintenance.voice_stop': { es: 'Parar la grabación', en: 'Stop recording' },
  'ops.maintenance.voice_recording': { es: 'Grabando: {seconds} de 20 segundos', en: 'Recording: {seconds} of 20 seconds' },
  'ops.maintenance.voice_saved': { es: 'Nota de voz lista', en: 'Voice note ready' },
  'ops.maintenance.voice_remove': { es: 'Quitar la nota de voz', en: 'Remove the voice note' },
  'ops.maintenance.voice_unsupported': {
    es: 'Este teléfono no puede grabar voz aquí. Escribe la nota.',
    en: 'This phone cannot record voice here. Type the note.',
  },
  'ops.maintenance.voice_denied': {
    es: 'No dejaste usar el micrófono. Escribe la nota.',
    en: 'The microphone was not allowed. Type the note.',
  },
  'ops.maintenance.send': { es: 'Enviar reporte', en: 'Send report' },
  'ops.maintenance.need_target': {
    es: 'Escoge qué es o dónde está.',
    en: 'Pick what it is or where it is.',
  },
  'ops.maintenance.another': { es: 'Hacer otro reporte', en: 'Make another report' },
  'ops.maintenance.queued': {
    es: 'Guardado en el teléfono. Sale solo cuando haya señal.',
    en: 'Saved on the phone. It goes out by itself when there is signal.',
  },
  'ops.maintenance.line_broken': { es: 'Dañado: {what}', en: 'Broken: {what}' },
  'ops.maintenance.line_broken_at': { es: 'Dañado en {where}: {what}', en: 'Broken at {where}: {what}' },
  'ops.maintenance.line_place': { es: 'Problema en {where}', en: 'Problem at {where}' },

  /* ---- Sistemas ---- */
  'ops.systems.pending_pill': { es: 'Sin grabar', en: 'Not recorded' },
  'ops.systems.intro': {
    es: 'Toca un equipo para ver qué hacer.',
    en: 'Tap a system to see what to do.',
  },
  'ops.systems.steps': { es: 'Haz esto', en: 'Do this' },
  'ops.systems.not_recorded': {
    es: 'Este paso todavía no está grabado.',
    en: 'This step has not been recorded yet.',
  },
  'ops.systems.not_recorded_help': {
    es: 'Cuando el jefe de operaciones lo grabe, aparece aquí. Mientras tanto, si algo falla, avisa.',
    en: 'When the Director of Operations records it, it shows up here. Until then, if something fails, report it.',
  },
  'ops.systems.video_placeholder': {
    es: 'El video de este equipo todavía no está.',
    en: 'The video for this system is not here yet.',
  },
  'ops.systems.data_title': { es: 'Lo que sabemos', en: 'What we know' },
  'ops.systems.where': { es: 'Dónde está', en: 'Where it is' },
  'ops.systems.make': { es: 'Marca y modelo', en: 'Make and model' },
  'ops.systems.report_this': { es: 'Reportar un problema con esto', en: 'Report a problem with this' },
  'ops.systems.no_assets': {
    es: 'Todavía no hay equipos en este teléfono. Conéctate una vez para bajarlos.',
    en: 'There are no systems on this phone yet. Connect once to download them.',
  },
  'ops.systems.not_found': { es: 'No encuentro este equipo.', en: 'I cannot find this system.' },
  'ops.systems.cat.power': { es: 'Energía', en: 'Power' },
  'ops.systems.cat.water': { es: 'Agua', en: 'Water' },
  'ops.systems.cat.marine': { es: 'Lanchas y muelles', en: 'Boats and docks' },
  'ops.systems.cat.hvac': { es: 'Aire', en: 'Air conditioning' },
  'ops.systems.cat.network': { es: 'Internet', en: 'Internet' },
  'ops.systems.cat.safety': { es: 'Seguridad', en: 'Safety' },
  'ops.systems.cat.building': { es: 'Casas', en: 'Buildings' },

  /* ---- Mensajes ---- */
  'ops.messages.title': { es: 'Mensajes de los pedidos', en: 'Request messages' },
  'ops.messages.pick': { es: 'Escoge un pedido para hablar de él.', en: 'Pick a request to talk about it.' },
  'ops.messages.empty_list': { es: 'No hay pedidos abiertos.', en: 'There are no open requests.' },
  'ops.messages.thread_title': { es: 'Mensajes del pedido', en: 'Messages for this request' },
  'ops.messages.empty_thread': { es: 'Nadie ha escrito todavía.', en: 'Nobody has written yet.' },
  'ops.messages.write': { es: 'Escribe tu mensaje', en: 'Write your message' },
  'ops.messages.send': { es: 'Enviar mensaje', en: 'Send message' },
  'ops.messages.not_translated': {
    es: 'Sin traducir todavía. Se ve en el idioma original.',
    en: 'Not translated yet. Shown in the original language.',
  },
  'ops.messages.updates': { es: 'Se actualiza solo cada 30 segundos.', en: 'Updates by itself every 30 seconds.' },
  'ops.messages.request_missing': { es: 'No encuentro este pedido.', en: 'I cannot find this request.' },
  'ops.messages.whatsapp': { es: 'Hablar por WhatsApp', en: 'Talk on WhatsApp' },
  'ops.messages.guests': { es: 'Mensajes de los huéspedes', en: 'Guest messages' },
  'ops.messages.guests_hint': {
    es: 'Un hilo por estadía. Tu respuesta se traduce para el huésped.',
    en: 'One thread per stay. Your reply is translated for the guest.',
  },
  'ops.messages.stay_missing': { es: 'No encuentro esta estadía.', en: 'I cannot find this stay.' },
  'ops.messages.stay_thread': { es: 'Hilo de la estadía', en: 'Stay thread' },
  'ops.messages.author_guest': { es: 'Huésped', en: 'Guest' },
  'ops.messages.author_me': { es: 'Tú', en: 'You' },
  'ops.messages.author_team': { es: 'Equipo', en: 'Team' },

  /* ---- Traductor ---- */
  'ops.translator.title': { es: 'Traductor', en: 'Translator' },
  'ops.translator.from_es': { es: 'De español a inglés', en: 'Spanish to English' },
  'ops.translator.from_en': { es: 'De inglés a español', en: 'English to Spanish' },
  'ops.translator.input': { es: 'Escribe aquí', en: 'Type here' },
  'ops.translator.go': { es: 'Traducir', en: 'Translate' },
  'ops.translator.result': { es: 'Traducción', en: 'Translation' },
  'ops.translator.copy': { es: 'Copiar', en: 'Copy' },
  'ops.translator.copied': { es: 'Copiado', en: 'Copied' },
  'ops.translator.dictate': { es: 'Hablar en vez de escribir', en: 'Speak instead of typing' },
  'ops.translator.dictate_stop': { es: 'Parar', en: 'Stop' },
  'ops.translator.dictate_none': {
    es: 'Este teléfono no puede escuchar. Escribe el texto.',
    en: 'This phone cannot listen. Type the text.',
  },
  'ops.translator.need_text': { es: 'Escribe algo primero.', en: 'Write something first.' },
  'ops.translator.cached': { es: 'Ya estaba traducido antes.', en: 'This was translated before.' },

  /* ---- Jefes: hub ---- */
  'ops.boss.stays': { es: 'Estadías', en: 'Stays' },
  'ops.boss.stays_hint': { es: 'Anotar y cambiar las estadías de los huéspedes.', en: 'Enter and edit guest stays.' },
  'ops.boss.phones': { es: 'Teléfonos', en: 'Phones' },
  'ops.boss.phones_hint': { es: 'Hacer un enlace para registrar un teléfono.', en: 'Make a link to pair a phone.' },
  'ops.boss.review': { es: 'Revisar limpiezas', en: 'Review cleanings' },
  'ops.boss.review_hint': { es: 'Aprobar o mandar a repetir.', en: 'Approve or send back.' },

  /* ---- Jefes: estadías ---- */
  'ops.stays.title': { es: 'Estadías', en: 'Stays' },
  'ops.stays.new': { es: 'Anotar una estadía nueva', en: 'Enter a new stay' },
  'ops.stays.edit': { es: 'Cambiar esta estadía', en: 'Edit this stay' },
  'ops.stays.none': { es: 'Todavía no hay estadías anotadas.', en: 'No stays entered yet.' },
  'ops.stays.code': { es: 'ID de reserva', en: 'Reservation ID' },
  'ops.stays.tier': { es: 'Tipo de estadía', en: 'Stay type' },
  'ops.stays.tier_all': { es: 'Todo incluido', en: 'All inclusive' },
  'ops.stays.tier_std': { es: 'Estándar', en: 'Standard' },
  'ops.stays.arrives': { es: 'Llega el', en: 'Arrives on' },
  'ops.stays.departs': { es: 'Se va el', en: 'Departs on' },
  'ops.stays.adults': { es: 'Adultos', en: 'Adults' },
  'ops.stays.children': { es: 'Niños', en: 'Children' },
  'ops.stays.flight': { es: 'Vuelo de llegada', en: 'Arrival flight' },
  'ops.stays.boat_time': { es: 'Hora de la lancha', en: 'Boat time' },
  'ops.stays.dietary': { es: 'Comida: alergias y gustos', en: 'Food: allergies and preferences' },
  'ops.stays.notes': { es: 'Notas internas', en: 'Internal notes' },
  'ops.stays.lead_first': { es: 'Nombre del huésped principal', en: 'Lead guest first name' },
  'ops.stays.lead_last': { es: 'Apellido del huésped principal', en: 'Lead guest last name' },
  'ops.stays.lead_last_help': {
    es: 'El huésped entra con su ID de reserva y este apellido.',
    en: 'The guest signs in with the Reservation ID and this last name.',
  },
  'ops.stays.status': { es: 'Estado', en: 'Status' },
  'ops.stays.status_upcoming': { es: 'Por llegar', en: 'Upcoming' },
  'ops.stays.status_in_house': { es: 'En la isla', en: 'On the island' },
  'ops.stays.status_departed': { es: 'Ya se fue', en: 'Departed' },
  'ops.stays.status_cancelled': { es: 'Cancelada', en: 'Cancelled' },
  'ops.stays.save': { es: 'Guardar estadía', en: 'Save stay' },
  'ops.stays.saved': { es: 'Estadía guardada.', en: 'Stay saved.' },
  'ops.stays.need_signal': {
    es: 'Para guardar una estadía necesitas señal. Prueba cuando vuelva.',
    en: 'You need signal to save a stay. Try again when it comes back.',
  },
  'ops.stays.denied': {
    es: 'El sistema no te dejó guardar. Entra con tu PIN en un teléfono registrado.',
    en: 'The database did not allow the save. Sign in with your PIN on a paired phone.',
  },
  'ops.stays.problem_dates': { es: 'La salida no puede ser antes de la llegada.', en: 'The departure cannot be before the arrival.' },
  'ops.stays.problem_missing': { es: 'Faltan las fechas.', en: 'The dates are missing.' },
  'ops.stays.problem_people': { es: 'Pon al menos una persona.', en: 'Enter at least one person.' },
  'ops.stays.problem_lead': { es: 'Falta el apellido del huésped.', en: 'The lead guest last name is missing.' },
  'ops.stays.people_line': { es: '{adults} adultos, {children} niños', en: '{adults} adults, {children} children' },
  'ops.stays.dates_line': { es: '{from} a {to}', en: '{from} to {to}' },
  'ops.stays.close': { es: 'Cerrar el formulario', en: 'Close the form' },

  /* ---- Jefes: teléfonos ---- */
  'ops.phones.title': { es: 'Registrar un teléfono', en: 'Pair a phone' },
  'ops.phones.who': { es: '¿De quién es el teléfono?', en: 'Whose phone is it?' },
  'ops.phones.label': { es: 'Nombre del teléfono (si quieres)', en: 'Phone name (optional)' },
  'ops.phones.make': { es: 'Hacer el enlace', en: 'Make the link' },
  'ops.phones.link': { es: 'Enlace para registrar', en: 'Pairing link' },
  'ops.phones.expires': {
    es: 'Sirve una sola vez y se vence en {minutes} minutos.',
    en: 'It works once and expires in {minutes} minutes.',
  },
  'ops.phones.copy': { es: 'Copiar el enlace', en: 'Copy the link' },
  'ops.phones.copied': { es: 'Enlace copiado', en: 'Link copied' },
  'ops.phones.whatsapp': { es: 'Mandar por WhatsApp', en: 'Send on WhatsApp' },
  'ops.phones.whatsapp_text': {
    es: 'Abre este enlace en tu teléfono para registrarlo en Dia Bonito Concierge Ops: {link}',
    en: 'Open this link on your phone to pair it with Dia Bonito Concierge Ops: {link}',
  },
  'ops.phones.qr_note': {
    es: 'El código QR todavía no está. Por ahora usa el enlace.',
    en: 'The QR code is not built yet. Use the link for now.',
  },
  'ops.phones.need_signal': { es: 'Para hacer el enlace necesitas señal.', en: 'You need signal to make the link.' },
  'ops.phones.denied': {
    es: 'No se pudo hacer el enlace. Solo un jefe o el dueño puede hacerlo.',
    en: 'The link could not be made. Only a manager or the owner can make one.',
  },
  'ops.phones.session_lost': {
    es: 'Tu sesión se cerró. Entra otra vez y prueba de nuevo.',
    en: 'Your session ended. Sign in again and try once more.',
  },
  'ops.phones.failed': {
    es: 'No se pudo hacer el enlace (error {status}). Prueba otra vez.',
    en: 'The link could not be made (error {status}). Try again.',
  },
  'ops.phones.pick_first': {
    es: 'Primero escoge de quién es el teléfono.',
    en: 'First pick whose phone it is.',
  },
  'ops.phones.copy_failed': {
    es: 'No se pudo copiar. Toca el enlace y cópialo tú.',
    en: 'Could not copy. Tap the link and copy it yourself.',
  },
  'ops.phones.no_people': {
    es: 'No veo personas en la lista. Conéctate una vez.',
    en: 'I see nobody in the list. Connect once.',
  },
  'ops.phones.role_crew': { es: 'equipo', en: 'crew' },
  'ops.phones.role_manager': { es: 'anfitrión', en: 'host' },
  'ops.phones.role_owner': { es: 'dueño', en: 'owner' },

  /* ---- Jefes: revisar ---- */
  'ops.review.title': { es: 'Revisar limpiezas', en: 'Review cleanings' },
  'ops.review.none': { es: 'No hay limpiezas esperando revisión.', en: 'No cleanings are waiting for review.' },
  'ops.review.approve': { es: 'Aprobar', en: 'Approve' },
  'ops.review.redo': { es: 'Mandar a repetir', en: 'Send back to redo' },
  'ops.review.note': { es: 'Nota para la persona (si quieres)', en: 'Note for the person (optional)' },
  'ops.review.done_by': { es: 'La hizo {name}', en: 'Done by {name}' },
  'ops.review.finished_at': { es: 'Terminó a las {time}', en: 'Finished at {time}' },
  'ops.review.sent': { es: 'Guardado.', en: 'Saved.' },
  'ops.review.evidence': { es: '{done} tarjetas hechas, {photos} fotos', en: '{done} cards done, {photos} photos' },
  'ops.review.redo_default_note': {
    es: 'Hay que repetir esta limpieza.',
    en: 'This cleaning has to be done again.',
  },

  // Habitaciones (migration 016). Appended, nothing above reworded.
  'ops.nav.rooms': { es: 'Asignación de cuartos', en: 'Room assignments' },

  'ops.rooms.title': { es: 'Asignación de cuartos', en: 'Room assignments' },
  'ops.rooms.lede': {
    es: 'Quién duerme en cada cuarto, y cuál hay que arreglar.',
    en: 'Who sleeps in each room, and which one needs work.',
  },
  'ops.rooms.state.occupied': { es: 'Ocupado', en: 'Occupied' },
  'ops.rooms.state.empty': { es: 'Vacío', en: 'Empty' },
  'ops.rooms.state.turnover': { es: 'Hay que dejarlo listo', en: 'Turnover needed' },
  'ops.rooms.state.service': { es: 'Pidieron limpieza', en: 'Service asked for' },
  'ops.rooms.futon_open': {
    es: 'Abre el futón y hazle la cama.',
    en: 'Open the futon and make it up.',
  },
  'ops.rooms.beds': { es: 'Camas', en: 'Beds' },
  'ops.rooms.en_suite': { es: 'Baño dentro', en: 'Bath inside' },
  'ops.rooms.people': { es: '{count} de {max} personas', en: '{count} of {max} people' },
  'ops.rooms.nobody': { es: 'Nadie', en: 'Nobody' },
  'ops.rooms.booker': { es: 'organiza', en: 'booked it' },
  'ops.rooms.photo_missing': {
    es: '[FOTO POR ENVIAR]',
    en: '[PHOTO TO SUPPLY]',
  },
  'ops.rooms.no_stay': {
    es: 'No hay nadie en la isla ahora.',
    en: 'Nobody is on the island right now.',
  },
  'ops.rooms.needs_signal': {
    es: 'Para mover a alguien necesitas señal.',
    en: 'You need signal to move somebody.',
  },
  'ops.rooms.move': { es: 'Mover', en: 'Move' },
  'ops.rooms.move_to': { es: 'Mover a {room}', en: 'Move to {room}' },
  'ops.rooms.release': { es: 'Sacar del cuarto', en: 'Take out of the room' },
  'ops.rooms.moved': { es: 'Listo.', en: 'Done.' },
  'ops.rooms.failed': { es: 'No se pudo. Intenta otra vez.', en: 'That did not work. Try again.' },
  'ops.rooms.boss_only': {
    es: 'Solo los anfitriones y el dueño mueven a la gente.',
    en: 'Only the hosts and the owner move people.',
  },

  'ops.rooms.optin_title': { es: 'Lista de promociones', en: 'Marketing list' },
  'ops.rooms.optin_lede': {
    es: 'Apúntalo solo si te dio permiso en persona. Aquí en la isla.',
    en: 'Add somebody only if they said yes in person, here at the property.',
  },
  'ops.rooms.optin_record': { es: 'Dio permiso en persona', en: 'Said yes in person' },
  'ops.rooms.optin_done': { es: 'Apuntado el {date}', en: 'Recorded on {date}' },
  'ops.rooms.optin_undo': { es: 'Quitar de la lista', en: 'Take off the list' },
  'ops.rooms.optin_no_email': {
    es: 'No dejó correo ni red social.',
    en: 'They left no email and no handle.',
  },
  'ops.rooms.optin_saved': { es: 'Guardado.', en: 'Saved.' },

  'ops.rooms.export': { es: 'Bajar la lista en CSV', en: 'Download the list as CSV' },
  'ops.rooms.export_empty': {
    es: 'Todavía no hay nadie en la lista.',
    en: 'Nobody is on the list yet.',
  },
  'ops.rooms.export_count': { es: '{count} personas', en: '{count} people' },
  'ops.rooms.export_owner_only': { es: 'Solo el dueño.', en: 'Owners only.' },

  'ops.rooms.edit_title': { es: 'Camas y capacidad', en: 'Beds and capacity' },
  'ops.rooms.edit_lede': {
    es: 'Estos números no son seguros. Arréglalos si están mal.',
    en: 'These numbers are assumptions. Correct them if they are wrong.',
  },
  'ops.rooms.edit_base': { es: 'Camas fijas (personas)', en: 'Own beds (people)' },
  'ops.rooms.edit_extra': { es: 'Futón (personas más)', en: 'Futon (extra people)' },
  'ops.rooms.edit_save': { es: 'Guardar el cuarto', en: 'Save the room' },
  'ops.rooms.edit_saved': { es: 'Guardado.', en: 'Saved.' },
  'ops.rooms.edit_owner_only': {
    es: 'Solo el dueño cambia estos números.',
    en: 'Only an owner changes these numbers.',
  },
  'ops.rooms.edit_total': {
    es: 'En la isla duermen {count} personas en total.',
    en: 'The island sleeps {count} in all.',
  },

  /* ---- Working a request, and answering the guest under it (phase 3b) ---- */
  'ops.request.work_title': { es: '¿Cómo va este pedido?', en: 'How is this request going?' },
  'ops.request.work_now': { es: 'Ahora: {status}', en: 'Now: {status}' },
  'ops.request.work.seen': { es: 'Ya lo vi', en: 'I saw it' },
  'ops.request.work.in_progress': { es: 'Lo estoy haciendo', en: 'I am on it' },
  'ops.request.work.done': { es: 'Ya está listo', en: 'It is done' },
  'ops.request.work_guest_sees': {
    es: 'El huésped ve este estado en su teléfono.',
    en: 'The guest sees this status on their phone.',
  },
  'ops.request.work_hosts_only': {
    es: 'El estado lo cambian los anfitriones.',
    en: 'The hosts change the status.',
  },
  'ops.messages.stay_hosts_only': {
    es: 'Aquí contestan los anfitriones. Tú puedes leer.',
    en: 'The hosts answer here. You can read.',
  },
  'ops.messages.team_only': { es: 'Solo el equipo ve estos mensajes.', en: 'Only the team sees these messages.' },
  'ops.messages.reply_guest_title': { es: 'Responder al huésped', en: 'Reply to the guest' },
  'ops.messages.reply_guest_hint': {
    es: 'El huésped ve tu respuesta debajo de su pedido. Se traduce sola.',
    en: 'The guest sees your reply under their request. It is translated for them.',
  },
  'ops.messages.reply_guest_label': { es: 'Escribe tu respuesta', en: 'Write your reply' },
  'ops.messages.reply_guest_send': { es: 'Enviar al huésped', en: 'Send to the guest' },
  'ops.messages.decline_checkbox': {
    es: 'Esta respuesta dice que no podemos hacer este pedido',
    en: 'This reply says we cannot do this request',
  },
  'ops.messages.reply_guest_send_decline': { es: 'Enviar y declinar el pedido', en: 'Send and decline the request' },
  'ops.messages.reply_guest_empty': { es: 'Todavía no le has respondido.', en: 'No reply to the guest yet.' },
  'ops.messages.reply_to_request': { es: 'Respuesta a un pedido', en: 'Reply to a request' },
} as const satisfies Section;
